import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SupportService } from '../../../core/services/support.service';
import { UserStateService, GlobalsService, OrderService } from '../../../core/services';
import { UserService } from '../../../core/services/user.service';
import { OrderDetailsDialogComponent } from '../../orders/order-details-dialog/order-details-dialog.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { SupportIconComponent } from '../../../shared/components/support-icon/support-icon.component';
import SparkMD5 from 'spark-md5';

@Component({
  selector: 'app-support-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    TranslateModule,
    SupportIconComponent
  ],
  template: `
    <div class="support-dialog">
      <!-- Header with gradient -->
      <div class="dialog-header">
        <div class="header-left">
          <div class="header-icon">
            <app-support-icon [size]="24" style="color: #fff;"></app-support-icon>
          </div>
          <h2>{{ 'SUPPORT.TITLE' | translate }}</h2>
        </div>
        <button type="button" class="close-btn" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="dialog-content">
        <!-- Email Section Card -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-icon">
              <mat-icon>email</mat-icon>
            </div>
            <span>{{ 'SUPPORT.EMAIL_LABEL' | translate }}</span>
          </div>
          <div class="input-row">
            <input
              type="email"
              autocomplete="off"
              [(ngModel)]="email"
              placeholder="name@example.com"
              (keydown.enter)="loginAsUser()">
            <button type="button" class="btn-action" (click)="loginAsUser()" [disabled]="loggingIn()">
              @if (loggingIn()) {
                <mat-spinner diameter="18"></mat-spinner>
              } @else {
                <mat-icon>login</mat-icon>
                {{ 'SUPPORT.LOGIN' | translate }}
              }
            </button>
          </div>

          <!-- Invalidate session toggle -->
          <div class="toggle-row">
            <div class="support-toggle" [class.active]="invalidateSession" (click)="invalidateSession = !invalidateSession">
              <div class="support-toggle-track">
                <div class="support-toggle-thumb"></div>
              </div>
              <span class="toggle-label">{{ 'SUPPORT.INVALIDATE_SESSION' | translate }}</span>
            </div>
          </div>
        </div>

        <!-- Order ID Section Card -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-icon">
              <mat-icon>receipt_long</mat-icon>
            </div>
            <span>{{ 'SUPPORT.ORDER_ID' | translate }}</span>
          </div>
          <div class="input-row">
            <input
              type="number"
              autocomplete="off"
              [(ngModel)]="orderId"
              placeholder="123456"
              (keydown.enter)="openOrder()">
            <button type="button" class="btn-action" (click)="openOrder()" [disabled]="loadingOrder()">
              @if (loadingOrder()) {
                <mat-spinner diameter="18"></mat-spinner>
              } @else {
                <mat-icon>open_in_new</mat-icon>
                {{ 'SUPPORT.OPEN' | translate }}
              }
            </button>
          </div>
        </div>

        <!-- File MD5 Section Card (admin only) -->
        @if (isAdmin) {
          <div class="section-card">
            <div class="section-header">
              <div class="section-icon">
                <mat-icon>description</mat-icon>
              </div>
              <span>{{ 'SUPPORT.FILE_MD5' | translate }}</span>
            </div>
            <div class="md5-row">
              <button type="button" class="btn-action btn-browse" (click)="fileInput.click()" [disabled]="computingMd5()">
                @if (computingMd5()) {
                  <mat-spinner diameter="18"></mat-spinner>
                } @else {
                  <mat-icon>upload_file</mat-icon>
                  {{ 'SUPPORT.BROWSE' | translate }}
                }
              </button>
              <input
                type="file"
                #fileInput
                hidden
                (change)="onFileSelected($event)">
              <div class="md5-result" [class.has-value]="md5Hash">
                <mat-icon>{{ md5Hash ? 'check_circle' : 'insert_drive_file' }}</mat-icon>
                <span>{{ md5Hash || ('SUPPORT.SELECT_FILE' | translate) }}</span>
              </div>
            </div>
            <p class="helper-text">
              <mat-icon>info</mat-icon>
              {{ 'SUPPORT.MD5_HELPER' | translate }}
            </p>
          </div>
        }

        <!-- Error message -->
        @if (errorMessage()) {
          <div class="error-message">
            <mat-icon>error</mat-icon>
            {{ errorMessage() }}
          </div>
        }

        <!-- Success message -->
        @if (successMessage()) {
          <div class="success-message">
            <mat-icon>check_circle</mat-icon>
            {{ successMessage() }}
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .support-dialog {
      width: 100%;
    }

    // Header with gradient
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      background: linear-gradient(135deg, #2f5a45 0%, #4a8a6a 50%, #5a9a7a 100%);

      .header-left {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .header-icon {
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        mat-icon {
          color: white;
          font-size: 22px;
          width: 22px;
          height: 22px;
        }
      }

      h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: white;
      }

      .close-btn {
        width: 36px;
        height: 36px;
        background: rgba(255, 255, 255, 0.15);
        border: none;
        border-radius: 10px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;

        mat-icon {
          color: white;
          font-size: 20px;
          width: 20px;
          height: 20px;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.25);
        }
      }
    }

    .dialog-content {
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    // Section Cards
    .section-card {
      background: white;
      border: 1.5px solid #eaeaea;
      border-radius: 16px;
      padding: 16px;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 14px;

      .section-icon {
        width: 32px;
        height: 32px;
        background: #e8f0eb;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: #696a69;
        }
      }

      mat-icon:not(.section-icon mat-icon) {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #696a69;
      }

      span {
        font-size: 13px;
        font-weight: 600;
        color: #3b3b3b;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
    }

    .section-body {
      // No extra padding needed since card has padding
    }

    .input-row {
      display: flex;
      gap: 12px;
      align-items: center;

      input[type="email"],
      input[type="number"] {
        flex: 1;
        padding: 14px 16px;
        border: 1.5px solid #eaeaea;
        border-radius: 12px;
        font-size: 15px;
        outline: none;
        transition: all 0.2s;
        background: #fafbfa;

        &:focus {
          border-color: #696a69;
          background: white;
          box-shadow: 0 0 0 3px rgba(60, 112, 86, 0.1);
        }

        &::placeholder {
          color: #a0b0a0;
        }
      }
    }

    .toggle-row {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #eaeaea;
    }

    .support-toggle {
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      user-select: none;

      .support-toggle-track {
        width: 48px;
        height: 26px;
        background: #d4ddd4;
        border-radius: 13px;
        position: relative;
        transition: all 0.2s ease;

        .support-toggle-thumb {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
      }

      .toggle-label {
        font-size: 14px;
        color: #5a6a5a;

        strong {
          font-weight: 600;
        }
      }

      &.active {
        .support-toggle-track {
          background: #696a69;

          .support-toggle-thumb {
            left: 25px;
          }
        }

        .toggle-label {
          color: #3b3b3b;
        }
      }

      &:hover .support-toggle-track {
        box-shadow: 0 0 0 3px rgba(60, 112, 86, 0.1);
      }
    }

    .btn-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 20px;
      background: #696a69;
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      min-width: 110px;
      transition: all 0.2s;
      box-shadow: 0 4px 12px rgba(60, 112, 86, 0.25);

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      &:hover:not(:disabled) {
        background: #2e5a44;
        box-shadow: 0 6px 16px rgba(60, 112, 86, 0.3);
        transform: translateY(-1px);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      mat-spinner {
        margin: 0;

        ::ng-deep circle {
          stroke: white !important;
        }
      }
    }

    // MD5 Section
    .md5-row {
      display: flex;
      gap: 12px;
      align-items: center;
    }

    .btn-browse {
      flex-shrink: 0;
    }

    .md5-result {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      background: #fafbfa;
      border: 1.5px dashed #d4ddd4;
      border-radius: 12px;
      min-height: 48px;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #a0b0a0;
        flex-shrink: 0;
      }

      span {
        font-size: 14px;
        color: #878787;
        word-break: break-all;
      }

      &.has-value {
        border-style: solid;
        border-color: #696a69;
        background: #f0f7f3;

        mat-icon {
          color: #696a69;
        }

        span {
          color: #3b3b3b;
          font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
          font-size: 12px;
        }
      }
    }

    .helper-text {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 12px 0 0 0;
      font-size: 12px;
      color: #878787;

      mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
    }

    // Messages
    .error-message,
    .success-message {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      border-radius: 12px;
      font-size: 14px;
      border: 1px solid;

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
    }

    .error-message {
      background: #fef2f2;
      color: #b91c1c;
      border-color: #fecaca;
    }

    .success-message {
      background: #f0fdf4;
      color: #166534;
      border-color: #bbf7d0;
    }
  `]
})
export class SupportDialogComponent {
  private dialogRef = inject(MatDialogRef<SupportDialogComponent>);
  private dialog = inject(MatDialog);
  private supportService = inject(SupportService);
  private userState = inject(UserStateService);
  private userService = inject(UserService);
  private orderService = inject(OrderService);
  private globals = inject(GlobalsService);
  private translate = inject(TranslateService);

  email = '';
  invalidateSession = false;
  orderId: number | null = null;
  md5Hash = '';

  /** Plain localStorage persistence of the LAST USED email / order id —
   *  survives refreshes on this machine without going through the browser's
   *  credential manager (inputs carry autocomplete="off"). */
  private static readonly LAST_USED_KEY = 'support-dialog-last-used';

  /** Optional launch data — e.g. the bug-report detail's per-email shortcut
   *  pre-fills the customer email so support starts in one click. */
  private readonly dialogData = inject<{ prefillEmail?: string } | null>(MAT_DIALOG_DATA, { optional: true });

  constructor() {
    try {
      const raw = localStorage.getItem(SupportDialogComponent.LAST_USED_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as { email?: string; orderId?: number };
        if (typeof saved.email === 'string') this.email = saved.email;
        if (typeof saved.orderId === 'number' && saved.orderId > 0) this.orderId = saved.orderId;
      }
    } catch { /* corrupt/unavailable storage — start empty */ }
    // A caller-provided email wins over the last-used one.
    if (this.dialogData?.prefillEmail) {
      this.email = this.dialogData.prefillEmail;
    }
  }

  /** Merge-persist one field the moment its action is used. */
  private persistLastUsed(patch: { email?: string; orderId?: number }): void {
    try {
      const raw = localStorage.getItem(SupportDialogComponent.LAST_USED_KEY);
      const saved = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        SupportDialogComponent.LAST_USED_KEY,
        JSON.stringify({ ...saved, ...patch })
      );
    } catch { /* storage full/unavailable — persistence is best-effort */ }
  }

  loggingIn = signal(false);
  loadingOrder = signal(false);
  computingMd5 = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  get isAdmin(): boolean {
    return this.globals.isAdmin;
  }

  async loginAsUser(): Promise<void> {
    if (!this.email?.trim()) {
      this.showError(this.translate.instant('SUPPORT.INVALID_EMAIL'));
      return;
    }

    this.clearMessages();
    this.loggingIn.set(true);
    this.persistLastUsed({ email: this.email.trim() });

    try {
      const result = await firstValueFrom(
        this.supportService.supportLogin(this.email.trim(), this.invalidateSession)
      );

      console.log('[SupportDialog] Login result:', result);

      // The /support/login response now nests the customer under `user`
      // ({ user, auth, impersonatedBy, _token }) per the integration spec.
      // Older deployments returned the customer fields at the top level —
      // fall back to that shape so a backend rollback doesn't break the UI.
      const customer = result?.user ?? result;
      if (customer && customer.id) {
        // Full support login: map response, set tokens, initialize session
        const user = await this.userService.handleSupportLogin(result);
        this.showSuccess(this.translate.instant('SUPPORT.LOGIN_SUCCESS', { name: user.firstName || user.email }));

        // Close dialog after short delay
        setTimeout(() => {
          this.dialogRef.close({ action: 'login', user: result });
        }, 1500);
      } else {
        this.showError(this.translate.instant('SUPPORT.USER_NOT_FOUND'));
      }
    } catch (error) {
      console.error('[SupportDialog] Login error:', error);
      this.showError(this.translate.instant('SUPPORT.LOGIN_ERROR'));
    } finally {
      this.loggingIn.set(false);
    }
  }

  async openOrder(): Promise<void> {
    if (!this.orderId) {
      this.showError(this.translate.instant('SUPPORT.INVALID_ORDER_ID'));
      return;
    }

    this.clearMessages();
    this.loadingOrder.set(true);
    this.persistLastUsed({ orderId: this.orderId });

    // "Impersonate by order" is a shortcut to "impersonate by username":
    // POST /support/login/by-order/:id parks the admin tokens, mints a
    // customer JWT, and returns the same { user, auth, ... } envelope as
    // the email-based login. The order-details dialog is then opened as
    // that customer, so its /orders/:id/details refetch (and every
    // downstream call) goes out with ownership intact — no more 404s.
    const orderId = this.orderId;
    try {
      const result = await firstValueFrom(
        this.supportService.loginByOrder(orderId)
      );

      const customer = result?.user ?? result;
      if (!customer?.id) {
        this.showError(this.translate.instant('SUPPORT.ORDER_NOT_FOUND'));
        return;
      }

      const user = await this.userService.handleSupportLogin(result);

      // Legacy orders (apiVersion === 1) were created by the old app and
      // can't be supported here. Now that the customer token is active we can
      // fetch the order; if it's legacy, show a notice and DON'T open the
      // order-details dialog.
      const details = await firstValueFrom(
        this.orderService.getOrderDetails({ id: orderId, orderId })
      );
      if (Number(details?.apiVersion) === 1) {
        this.dialogRef.close({ action: 'login', user: result });
        this.dialog.open(ConfirmDialogComponent, {
          width: '450px',
          panelClass: 'confirm-dialog-panel',
          data: {
            title: this.translate.instant('SUPPORT_REPAIR.LEGACY_ORDER_TITLE'),
            message: this.translate.instant('SUPPORT_REPAIR.LEGACY_ORDER_MESSAGE'),
            icon: 'info',
            confirmText: this.translate.instant('COMMON.OK'),
            cancelText: null
          }
        });
        return;
      }

      this.showSuccess(this.translate.instant('SUPPORT.LOGIN_SUCCESS', { name: user.firstName || user.email }));

      // Close the support dialog first so the order-details dialog
      // doesn't stack on top of it.
      this.dialogRef.close({ action: 'login', user: result });

      // Pass only the id — the dialog refetches via getOrderDetails
      // under the customer's now-active token.
      this.dialog.open(OrderDetailsDialogComponent, {
        width: '1000px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: { order: { id: orderId, orderId } },
        panelClass: 'order-details-dialog-panel'
      });
    } catch (error: any) {
      console.error('[SupportDialog] loginByOrder error:', error);
      const code = error?.error?.error;
      const key = code === 'ORDER_OR_CUSTOMER_NOT_FOUND'
        ? 'SUPPORT.ORDER_NOT_FOUND'
        : 'SUPPORT.ORDER_ERROR';
      this.showError(this.translate.instant(key));
    } finally {
      this.loadingOrder.set(false);
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    this.computingMd5.set(true);
    this.md5Hash = '';

    try {
      const md5 = await this.computeMD5(file);
      this.md5Hash = md5;
    } catch (error) {
      console.error('[SupportDialog] MD5 computation error:', error);
      this.showError(this.translate.instant('SUPPORT.MD5_ERROR'));
    } finally {
      this.computingMd5.set(false);
      // Reset file input
      input.value = '';
    }
  }

  private computeMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const spark = new SparkMD5.ArrayBuffer();
      const reader = new FileReader();

      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        spark.append(buffer);
        resolve(spark.end());
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  private showError(message: string): void {
    this.errorMessage.set(message);
    this.successMessage.set('');
  }

  private showSuccess(message: string): void {
    this.successMessage.set(message);
    this.errorMessage.set('');
  }

  private clearMessages(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  close(): void {
    this.dialogRef.close();
  }
}
